import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import MovieCard from "../components/MovieCard";
import { tmdbService } from "../services/tmdbService";

const Home = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [tmdbMovies, setTmdbMovies] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPopularMovies();
  }, []);

  const fetchPopularMovies = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Fetch 2 pages to get at least 40 movies, then show top 18
      const page1 = await tmdbService.getPopularMovies(1);
      const page2 = await tmdbService.getPopularMovies(2);
      const allMovies = [...page1.results, ...page2.results];
      setTmdbMovies(allMovies.slice(0, 18));
    } catch (err) {
      setError("Failed to load movies");
      console.error("Error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    setError(null);
    try {
      const data = await tmdbService.searchMovies(searchQuery);
      setSearchResults(data.results);
    } catch (err) {
      setError("Search failed. Please try again.");
      console.error("Search Error:", err);
    } finally {
      setIsSearching(false);
    }
  };

  const convertMovie = (tmdbMovie) => ({
    id: `tmdb-${tmdbMovie.id}`,
    title: tmdbMovie.title,
    description: tmdbMovie.overview,
    genre: tmdbMovie.genres?.map(g => g.name).slice(0, 2).join(', ') || 'Unknown',
    year: tmdbMovie.release_date ? parseInt(tmdbMovie.release_date.split('-')[0]) : 0,
    poster: tmdbService.getImageUrl(tmdbMovie.poster_path, 'w500'),
    trailer: tmdbService.extractTrailer(tmdbMovie.videos) || '',
    rating: Math.min(Math.max(tmdbMovie.vote_average / 2, 0), 5).toFixed(1),
    backdrop: tmdbService.getImageUrl(tmdbMovie.backdrop_path, 'w1280'),
    // Keep original TMDB data for favorites
    tmdbId: tmdbMovie.id,
    poster_path: tmdbMovie.poster_path,
    vote_average: tmdbMovie.vote_average,
    release_date: tmdbMovie.release_date,
    genres: tmdbMovie.genres,
    videos: tmdbMovie.videos
  });

  const displayMovies = searchResults.length > 0 ? searchResults.map(convertMovie) : tmdbMovies.map(convertMovie);

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Hero Section - Enhanced with Movie Poster */}
      {/* Hero Section - Advanced Premium Design */}
      <section className="relative py-20 lg:py-32 bg-[#050f0d] overflow-hidden flex flex-col justify-center items-center px-6 text-center isolate">
        {/* Animated grid background */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(30,224,152,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(30,224,152,0.03)_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,#000_20%,transparent_100%)] pointer-events-none"></div>

        {/* Diagonal background line */}
        <div className="absolute top-1/2 left-1/2 w-[200vw] h-[3px] bg-gradient-to-r from-transparent via-[#1ee098]/40 to-transparent -translate-x-1/2 -translate-y-1/2 -rotate-[35deg] pointer-events-none filter blur-[1px]"></div>

        {/* Floating animated light orbs */}
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#1ee098] rounded-full mix-blend-screen filter blur-[120px] opacity-20 animate-blob pointer-events-none"></div>
        <div className="absolute top-1/2 left-1/4 w-72 h-72 bg-blue-500 rounded-full mix-blend-screen filter blur-[120px] opacity-10 animate-blob pointer-events-none" style={{animationDelay: '2s'}}></div>
        <div className="absolute -bottom-32 left-1/2 w-96 h-96 bg-purple-500 rounded-full mix-blend-screen filter blur-[120px] opacity-10 animate-blob pointer-events-none" style={{animationDelay: '4s'}}></div>

        <div className="relative z-10 w-full max-w-5xl mx-auto flex flex-col items-center">
          <h1 className="text-5xl sm:text-7xl lg:text-[7.5rem] font-medium leading-[1.05] mb-8 text-white tracking-tight animate-float">
            Discover <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400 hover:from-[#1ee098] hover:to-blue-400 transition-all duration-700">better</span><br />
            movie watching<br />
            <span className="italic font-light opacity-90">experiences</span>
          </h1>

          <p className="text-lg md:text-xl text-gray-400/90 max-w-2xl mb-12 font-light leading-relaxed">
            Looking to find the perfect film for your weekend? We fix that. Search thousands of movies and let us tailor your next favorite viewing.
          </p>

          <div className="flex flex-col sm:flex-row items-center gap-6 w-full max-w-2xl justify-center">
            {/* The primary button mimicking the image with hover animations */}
            <Link to="/movies" className="group relative inline-flex items-center justify-center bg-[#1ee098] hover:bg-[#15c080] text-[#071512] px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 whitespace-nowrap shadow-[0_0_40px_rgba(30,224,152,0.3)] hover:shadow-[0_0_60px_rgba(30,224,152,0.5)] hover:-translate-y-1 overflow-hidden">
              <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-in-out"></div>
              <span className="relative z-10 flex items-center">
                Explore Movies <span className="ml-2 group-hover:translate-x-1 transition-transform duration-300 font-bold">›</span>
              </span>
            </Link>

            {/* Secondary discrete search bar with pulsing border focus */}
            <form onSubmit={handleSearch} className="w-full sm:w-auto flex-1 group">
              <div className="relative flex items-center bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-full p-2 transition-all duration-500 hover:bg-white/[0.06] focus-within:bg-white/[0.08] focus-within:border-[#1ee098] focus-within:shadow-[0_0_30px_rgba(30,224,152,0.15)] overflow-hidden">
                <input 
                  type="text" 
                  value={searchQuery} 
                  onChange={(e) => setSearchQuery(e.target.value)} 
                  placeholder="Search titles..." 
                  className="w-full bg-transparent text-white placeholder-gray-500/80 px-6 py-2.5 focus:outline-none text-base"
                />
                <button type="submit" disabled={isSearching || !searchQuery.trim()} className="bg-[#1ee098]/10 text-[#1ee098] rounded-full p-3 hover:bg-[#1ee098] hover:text-[#071512] transition-all duration-300 disabled:opacity-50 border border-[#1ee098]/20 disabled:hover:bg-[#1ee098]/10 disabled:hover:text-[#1ee098]">
                  {isSearching ? (
                    <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Movies Grid Section */}
      <section className="py-20 bg-gray-950">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Popular Movies</h2>
            <p className="text-gray-400">Discover the latest trending films</p>
          </div>

          {isLoading ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-xl text-gray-400">Loading movies...</p>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-red-400 mb-4">{error}</p>
              <button onClick={fetchPopularMovies} className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-all">Try Again</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
              {displayMovies.slice(0, 18).map((movie, index) => (
                <div key={movie.id} className="animate-slide-up" style={{animationDelay: `${index * 50}ms`}}>
                  <MovieCard movie={movie} />
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-16">
            <Link to="/movies" className="inline-flex items-center space-x-2 bg-gray-800 hover:bg-gray-700 text-white text-lg px-8 py-4 rounded-lg transition-all border border-gray-700">
              <span>View All Movies</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
